"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { SWRConfig } from "swr";
import { refreshGate } from "@/lib/swr-refresh-gate";
import { PortfolioProvider, usePortfolioContext } from "@/contexts/portfolio-context";
import { Header } from "./header";
import { PortfolioTabs } from "./portfolio-tabs";
import { BottomNav } from "./bottom-nav";
import { MarketIndices } from "@/components/holding/market-indices";
import { MarketStatusBar } from "./market-status-bar";

// iPad/iPadOS 검출 — viewport와 무관하게 데스크톱 UI 강제
function isIPadDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) return true;
  return false;
}

// 선택된 포트폴리오가 momentum 전략이면 body 에 'momentum-theme' 클래스 토글
// → 전체 화면 바탕색을 붉은 계열로 (globals.css 에서 정의). provider 내부에서만 동작.
function MomentumThemeBinder() {
  const { portfolios, selectedPortfolio } = usePortfolioContext();
  const isMomentum = portfolios.find((p) => p.name === selectedPortfolio)?.strategy === "momentum";
  useEffect(() => {
    const cls = "momentum-theme";
    if (isMomentum) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [isMomentum]);
  return null;
}

export function ClientLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // 클라이언트 hydration 직후 iPad 여부 판별 → body에 클래스 추가
  useEffect(() => {
    if (isIPadDevice()) {
      document.documentElement.classList.add("force-desktop");
    }
  }, []);

  // 로그인 페이지: 헤더·포트폴리오 탭·시장 라인·하단 네비 없이 로그인 박스만 표시.
  if (pathname === "/login") {
    return <>{children}</>;
  }

  return (
    // refreshGate: /holding 외 라우트에선 모든 SWR 훅의 주기적 refresh 를 차단 (초기 로딩은 유지)
    <SWRConfig value={{ use: [refreshGate] }}>
    <PortfolioProvider>
      <MomentumThemeBinder />
      {/* 상단 고정 그룹 — 시장 지수 + 포트폴리오 탭 + 데스크톱 네비. 스크롤 시 한 덩어리로 sticky. */}
      <div className="sticky top-0 z-50">
        {/* 시장 지수 — 데스크톱(md+) 또는 가로(landscape)에서 표시. 모바일 세로(portrait)는 숨김 */}
        <div className="hidden md:block landscape:block w-full px-6 pt-2 border-b border-black/[0.04]" style={{ background: "#f5f5f7" }}>
          <MarketIndices />
        </div>
        {/* 투자자 동향 + 시장 국면 — 환율/코스피 라인 바로 아래, 전 페이지 공통 */}
        <MarketStatusBar />
        <PortfolioTabs />
        <Header />
      </div>
      <main className="pb-[calc(56px+env(safe-area-inset-bottom))] md:pb-0">
        {children}
      </main>
      <BottomNav />
    </PortfolioProvider>
    </SWRConfig>
  );
}
